// src/api/chat.js
/**
 * Chat & Conversation API Layer
 * 
 * Handles all chat-related network communication including:
 * - Message submission and routing
 * - WebSocket streaming
 * - Conversation history management
 */
import { 
    INTAKE_API_URL, 
    GETTER_API_URL, 
    WEBSOCKET_API_URL, 
    HISTORY_API_URL 
} from '../config';
import { getIdToken, getAuthHeaders, handleAuthError } from './auth';

/**
 * Submits the initial user prompt to the "intake" API endpoint.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} prompt - The user's typed message.
 * @param {string|null} conversationId - The ID of the ongoing conversation, if it exists.
 * @param {Array} files - Array of uploaded file metadata.
 * @returns {Promise<object>} - Server response with task_id for polling.
 */
export const submitPrompt = async (prompt, conversationId, files = [], connectionId = null) => {
    console.log('CHAT API: Submitting prompt with authentication...');

    try {
        const headers = await getAuthHeaders();

        const payload = {
            prompt,
            conversationId,
            files: files.map(f => ({
                file_id: f.id,
                filename: f.name,
                file_type: f.type,
                file_size: f.size,
                s3_key: f.s3Key
            })),
            ...(connectionId && { connectionId })
        };

        const response = await fetch(INTAKE_API_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'prompt submission');
        }

        if (!response.ok) {
            throw new Error(`Intake API responded with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('CHAT API: Prompt submitted successfully');
        return data;
        
    } catch (error) {
        console.error('CHAT API: Failed to submit prompt:', error);
        throw error;
    }
};

/**
 * Fetches the result of an asynchronous routing task.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} taskId - The unique ID of the task.
 * @returns {Promise<object>} - The routing result from the server.
 */
export const fetchRoutingResult = async (taskId) => {
    console.log('CHAT API: Fetching routing result with authentication...');
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${GETTER_API_URL}?task_id=${taskId}`, {
            headers
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'routing result fetch');
        }

        if (!response.ok) {
            let errorData = {};
            try {
                errorData = await response.json();
            } catch (jsonError) {
                console.warn('CHAT API: Could not parse error response as JSON:', jsonError);
                errorData = { message: 'Unknown error occurred' };
            }
            
            const error = new Error(`API Error: ${response.status}`);
            error.status = response.status;
            error.data = errorData;
            throw error;
        }

        const data = await response.json();
        console.log('CHAT API: Routing result fetched successfully');
        return data;
        
    } catch (error) {
        console.error('CHAT API: Failed to fetch routing result:', error);
        throw error;
    }
};

/**
 * Establishes a WebSocket for real-time communication.
 * WITH AUTHENTICATION: Includes JWT token as query parameter.
 * 
 * ⚠️ SECURITY NOTE: JWT tokens in URLs are less secure than headers.
 * This is a WebSocket limitation. The token may appear in server logs.
 * Consider implementing token rotation for long-lived connections.
 * 
 * @param {object} params - Core parameters for the answer stream.
 * @param {object} callbacks - Functions to handle WebSocket events.
 * @returns {Promise<WebSocket>} - The live WebSocket instance.
 */
export const createStreamingSocket = async (
    { prompt, chosenModel, conversationId, files = [], enableSearch = false, routingDetails = null }, 
    { onMessage, onClose, onError }
) => {
    console.log('CHAT API: Creating authenticated WebSocket connection...');
    
    try {
        // Use the ID token for WebSocket authorization.
        // ID tokens always carry an `aud` claim equal to the Cognito App Client ID,
        // which is what the backend's jwt.decode(audience=...) validates against.
        // Regular access tokens and Google OAuth v2 access tokens do NOT have `aud`,
        // so they are rejected by the backend validator.
        const token = await getIdToken();
        const wsUrl = `${WEBSOCKET_API_URL}?token=${encodeURIComponent(token)}`;
        console.log('CHAT API: Connecting to WebSocket with authentication token');
        
        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            console.log('CHAT API: WebSocket connected, sending authenticated request...');
            const payload = { 
                action: 'getAnswer', 
                prompt, 
                chosenModel, 
                conversationId,
                files: files.map(f => ({
                    name: f.name,
                    size: f.size,
                    s3_key: f.s3Key
                })),
                enableSearch: enableSearch,
                routingDetails: routingDetails
            };
            socket.send(JSON.stringify(payload));
        };

        socket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            onMessage(data);

            if (data.status === 'done') {
                console.log('CHAT API: WebSocket stream complete');
                socket.close();
            }
        };

        socket.onclose = () => {
            console.log('CHAT API: WebSocket connection closed');
            onClose();
        };

        socket.onerror = (error) => {
            console.error('CHAT API: WebSocket error:', error);
            onError(error);
        };

        return socket;
        
    } catch (error) {
        console.error('CHAT API: Failed to create WebSocket:', error);
        // Call error callback immediately if we can't even create the socket
        onError(error);
        throw error;
    }
};

/**
 * Opens a short-lived WebSocket to receive the routing result push from the worker.
 * After onopen, sends {action: 'ping'} so the $default route can push back the connectionId
 * on an already-established connection (avoids post_to_connection-during-$connect issues).
 *
 * Returns a cancel() function. Call it to silently close the socket without triggering
 * any callbacks — used by stopGenerating, startNewChat, loadConversationInChat.
 *
 * Fallback behaviour:
 *  - Unexpected disconnect before result  → onTimeout() fires immediately
 *  - No result within 10 seconds          → onTimeout() fires
 */
export const createRoutingSocket = async ({ onConnectionId, onRoutingResult, onTimeout, onError }) => {
    console.log('CHAT API: Opening routing WebSocket...');
    try {
        const token = await getIdToken();
        const wsUrl = `${WEBSOCKET_API_URL}?token=${encodeURIComponent(token)}`;
        const socket = new WebSocket(wsUrl);
        let fallbackTimer = null;
        let resultReceived = false;

        const cancel = () => {
            if (resultReceived) return;
            resultReceived = true;
            clearTimeout(fallbackTimer);
            if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };

        socket.onopen = () => {
            console.log('CHAT API: Routing WebSocket open, requesting connectionId via ping...');
            socket.send(JSON.stringify({ action: 'ping' }));
            fallbackTimer = setTimeout(() => {
                if (resultReceived) return;
                resultReceived = true;
                console.log('CHAT API: Routing push timeout (10 s). Triggering polling fallback.');
                socket.close();
                onTimeout();
            }, 10000);
        };

        socket.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'connected') {
                onConnectionId(data.connectionId);
                // Socket stays open; routing_result push will follow
            } else if (data.type === 'routing_result') {
                if (resultReceived) return;
                resultReceived = true;
                clearTimeout(fallbackTimer);
                socket.close();
                onRoutingResult(data);
            }
        };

        socket.onclose = () => {
            console.log('CHAT API: Routing WebSocket closed.');
            clearTimeout(fallbackTimer);
            if (!resultReceived) {
                // Unexpected close (network drop, server restart, etc.)
                resultReceived = true;
                onTimeout();
            }
        };

        socket.onerror = (error) => {
            console.error('CHAT API: Routing WebSocket error:', error);
            clearTimeout(fallbackTimer);
            if (!resultReceived) {
                resultReceived = true;
                onError(error);
            }
        };

        return cancel;

    } catch (error) {
        // getIdToken() failed — let the caller's catch block handle it, don't call onError here
        console.error('CHAT API: Failed to create routing WebSocket:', error);
        throw error;
    }
};

/**
 * Fetches the list of conversations for the authenticated user.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @returns {Promise<Array>} - Array of conversation metadata objects.
 */
export const fetchConversations = async () => {
    console.log('CHAT API: Fetching conversations list with authentication...');
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(HISTORY_API_URL, {
            headers
        });

        if (response.status === 401) {
            handleAuthError(response.status, 'conversations fetch');
        }

        if (!response.ok) {
            throw new Error(`History API responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`CHAT API: Fetched ${data.length} conversations successfully`);
        return data;
        
    } catch (error) {
        console.error('CHAT API: Failed to fetch conversations:', error);
        throw error;
    }
};

/**
 * Fetches the complete message history for a specific conversation.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} conversationId - The ID of the conversation.
 * @returns {Promise<Array>} - Array of message objects.
 */
export const fetchConversationMessages = async (conversationId) => {
    console.log('CHAT API: Fetching conversation messages with authentication...');
    
    if (!conversationId) {
        console.error('CHAT API: No conversationId provided');
        throw new Error('Conversation ID is required');
    }
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${HISTORY_API_URL}/${conversationId}`, {
            headers
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'conversation messages fetch');
        }
        
        if (response.status === 404) {
            console.error('CHAT API: Conversation not found - 404 response');
            throw new Error('Conversation not found.');
        }

        if (!response.ok) {
            throw new Error(`History API responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`CHAT API: Fetched ${data.length} messages for conversation ${conversationId}`);
        return data;
        
    } catch (error) {
        console.error('CHAT API: Failed to fetch conversation messages:', error);
        throw error;
    }
};

/**
 * Renames a conversation.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} conversationId - The ID of the conversation to rename.
 * @param {string} newTitle - The new title for the conversation.
 * @returns {Promise<object>} - The updated conversation object.
 */
export const renameConversation = async (conversationId, newTitle) => {
    console.log('CHAT API: Renaming conversation with authentication...');
    
    if (!conversationId || !newTitle) {
        console.error('CHAT API: Missing required parameters for rename');
        throw new Error('Conversation ID and new title are required');
    }
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${HISTORY_API_URL}/${conversationId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ title: newTitle })
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'conversation rename');
        }
        
        if (response.status === 404) {
            console.error('CHAT API: Conversation not found - 404 response');
            throw new Error('Conversation not found.');
        }
        
        if (response.status === 400) {
            const error = await response.json();
            throw new Error(error.error || 'Invalid request');
        }

        if (!response.ok) {
            throw new Error(`History API responded with status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`CHAT API: Successfully renamed conversation ${conversationId}`);
        return data;
        
    } catch (error) {
        console.error('CHAT API: Failed to rename conversation:', error);
        throw error;
    }
};

/**
 * Deletes a conversation and all its messages.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} conversationId - The ID of the conversation to delete.
 * @returns {Promise<void>}
 */
export const deleteConversation = async (conversationId) => {
    console.log('CHAT API: Deleting conversation with authentication...');
    
    if (!conversationId) {
        console.error('CHAT API: No conversationId provided for deletion');
        throw new Error('Conversation ID is required');
    }
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${HISTORY_API_URL}/${conversationId}`, {
            method: 'DELETE',
            headers
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'conversation deletion');
        }
        
        if (response.status === 404) {
            console.error('CHAT API: Conversation not found - 404 response');
            throw new Error('Conversation not found.');
        }

        // 204 No Content is a valid success response for DELETE
        if (response.status !== 204 && !response.ok) {
            throw new Error(`History API responded with status: ${response.status}`);
        }
        
        console.log(`CHAT API: Successfully deleted conversation ${conversationId}`);
        
    } catch (error) {
        console.error('CHAT API: Failed to delete conversation:', error);
        throw error;
    }
};